import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookmarkIcon, HeartIcon, MapPinIcon, MessageCircleIcon, Share2Icon } from 'lucide-react';
import { ActivityContext } from './ActivityContext';
import { CommentGlyph } from './icons/BeelyvIcons';
import { Avatar } from './Avatar';
import { findPerson } from '../data/people';
import { useLocale } from '../contexts/LocaleContext';
import { useSocial } from '../contexts/SocialContext';
import type { Post } from '../types';
export function PostCard({
  post,
  clickable = true
}: {
  post: Post;
  clickable?: boolean;
}) {
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const social = useSocial();
  const author = findPerson(post.authorId);
  const [shared, setShared] = useState(false);
  useEffect(() => {
    if (!shared) return;
    const timer = window.setTimeout(() => setShared(false), 2400);
    return () => window.clearTimeout(timer);
  }, [shared]);
  const open = () => navigate(`/post/${post.id}`);
  return <article className="rounded-card border border-sand-line bg-white/70 p-2" data-id="element-1227" data-mp="2gb4t9j5guwhf">
      <header className="flex items-center gap-2" data-id="element-1228" data-mp="2kbot4uhfce79">
        <Avatar src={author.avatar} name={author.name} size={38} onClick={() => navigate(`/u/${author.username.replace('@', '')}`)} label={author.name} data-id="element-1229" data-mp="2kdjgsgk2ykf1" />
        <div className="min-w-0 flex-1" data-id="element-1230" data-mp="1wlufwbt70nmh">
          <p className="truncate text-[13.5px] font-medium text-deep" data-id="element-1231" data-mp="1wkt23zksln12">
            {author.name}
          </p>
          <p className="truncate text-[11px] text-muted" data-id="element-1232" data-mp="21lqji9xzad1i">
            {author.username} · {t('post.hoursAgo', {
            n: post.hoursAgo
          })}
          </p>
        </div>
      </header>

      <div className="mt-2" data-id="element-1233" data-mp="1sjhbbqq6mvra">
        <ActivityContext context={post.context} data-id="element-1234" data-mp="1sksp8jdcdjw4" />
        <p className="mt-1 text-[13.5px] leading-relaxed text-deep" data-id="element-1235" data-mp="3hzgrhrs7xlgf">
          {tl(post.caption)}
        </p>
      </div>

      <div className="relative mt-2 overflow-hidden rounded-[14px]" data-id="element-1236" data-mp="3myjxpfjxpxpz">
        {clickable ? <button type="button" onClick={open} className="block w-full" aria-label={t('post.open')} data-id="element-1237" data-mp="3kjviulakzzmf">
            <img src={post.media} alt={tl(post.caption)} className="aspect-[4/3] w-full object-cover" data-id="element-1238" data-mp="3gkeank3rsodk" />
          </button> : <img src={post.media} alt={tl(post.caption)} className="aspect-[4/3] w-full object-cover" data-id="element-1239" data-mp="36lymnq01hv8a" />}

        {post.context.activity && <span className="bee-num pointer-events-none absolute bottom-2 left-2 rounded-pill bg-deep/85 px-2 py-1 text-[11px] font-medium text-sand" data-id="element-1240" data-mp="3589mr61ihx3">
            {tl(post.context.activity)}
          </span>}
      </div>

      {post.location && <p className="mt-1 flex items-center gap-1 text-[11px] text-muted" data-id="element-1241" data-mp="qtrib7t04wpp">
          <MapPinIcon size={12} aria-hidden="true" data-id="element-1242" data-mp="m29du9rrsdzz" />
          {tl(post.location)}
        </p>}

      <footer className="mt-2 flex items-center justify-between" data-id="element-1243" data-mp="tdnubmd0g5xx">
        <div className="flex items-center gap-1" data-id="element-1244" data-mp="tf8m547xxas3">
          <button type="button" onClick={() => social.toggleLike(post.id)} aria-pressed={social.isLiked(post.id)} aria-label={t('post.like')} className={`flex min-h-[44px] items-center gap-1 rounded-pill px-2 text-[12px] font-medium transition-colors duration-150 ease-brand ${social.isLiked(post.id) ? 'text-gold-deep' : 'text-muted hover:text-deep'}`} data-id="element-1245" data-mp="yda6rtl0li5o">
            <HeartIcon size={17} fill={social.isLiked(post.id) ? 'currentColor' : 'none'} data-id="element-1246" data-mp="1m19zv05stknf" />
            <span className="bee-num" data-id="element-1247" data-mp="1r0d3qha1lktt">{social.likeCount(post)}</span>
          </button>

          <button type="button" onClick={() => navigate(`/post/${post.id}/comentarios`)} aria-label={t('post.comment')} className="flex min-h-[44px] items-center gap-1 rounded-pill px-2 text-[12px] font-medium text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-1248" data-mp="1w1aj6m3o40rm">
            <CommentGlyph size={18} data-id="element-1249" data-mp="2spjq77qb71qc" />
            <span className="bee-num" data-id="element-1250" data-mp="36j0xnu5q0o00">{social.commentCount(post.id)}</span>
          </button>
        </div>

        <div className="flex items-center gap-1" data-id="element-1251" data-mp="32i8of0wumj3q">
          {shared && <span className="text-[11px] font-medium text-gold-deep" data-id="element-1252" data-mp="2erbl2cj4cu43">
              {t('post.shared')}
            </span>}
          <button type="button" onClick={() => setShared(true)} aria-label={t('post.share')} className="grid h-11 w-11 place-items-center rounded-pill text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-1253" data-mp="29ttnfiajy1be">
            <Share2Icon size={17} data-id="element-1254" data-mp="319aqu6fyu8o" />
          </button>
          <button type="button" onClick={() => social.toggleSave(post.id)} aria-pressed={social.isSaved(post.id)} aria-label={t('post.save')} className={`grid h-11 w-11 place-items-center rounded-pill transition-colors duration-150 ease-brand ${social.isSaved(post.id) ? 'text-gold-deep' : 'text-muted hover:text-deep'}`} data-id="element-1255" data-mp="34574e2n5av0">
            <BookmarkIcon size={17} fill={social.isSaved(post.id) ? 'currentColor' : 'none'} data-id="element-1256" data-mp="1ly6w1s5pbgw" />
          </button>
        </div>
      </footer>
    </article>;
}